import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, Button, VStack, useToast } from "@chakra-ui/react";
import { Formik, Form, Field } from "formik";
import * as R from "ramda";
import * as Yup from "yup";

import { FLOW_STATE, isBooleanTrue, isTrue, useSessionCtx, noop, useAnalytics } from "utils";
import { ALLOWBYPASSTOS, REQUESTBYPASSTOS } from "utils/constants";
import { isSignedTOS, useSignTOS, useTOSStatus } from "api/tos";
import EmptyState from "components/EmptyState";
import { InvalidLinkEmptyState, useInvalidLinkCopy } from "components/EmptyState/InvalidLink";
import { ConsentView } from "./ConsentView";
import TiliaAPIError from "api/TiliaAPIError";
import { useFlowConfig } from "flows/hooks";
import Header from "flows/components/Header";

const MIN_HEIGHT = "320px"; // in the case of widget script being used with 'resize: true', it's possible the height for the TOS/Privacy modal to only be the height of the TOS flow UI.  With this, we make sure there's enough height for modal to not look weird/small

const FormContents = ({ isSubmitting, values }) => {
  const { formatMessage } = useIntl();
  const tosErrorMessage = formatMessage({
    defaultMessage: "You must agree to the Terms of Service and Privacy Policy to continue",
    description: "You must aerror message",
  });
  const submitDisabled = isSubmitting || values.tosAndPpCheckbox !== true; // TODO: fix formik isValid not working as expected.  Sometimes tosAndPpCheckbox is true (which is all isValid needs) and yet there is stil an error for it
  return (
    <VStack data-testid="tos-form" as={Form} spacing={6} align="stretch">
      <VStack spacing={2} align="stretch">
        <Field name="tosAndPpCheckbox">
          {({ field, meta }) => (
            <ConsentView
              checked={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              // No style props: this page is the consent, so it keeps Chakra's full-size defaults
              error={meta.touched && meta.error && tosErrorMessage}
            />
          )}
        </Field>
      </VStack>

      <Button
        data-testid="submitButton"
        isDisabled={submitDisabled}
        isLoading={isSubmitting}
        loadingText={formatMessage({
          defaultMessage: "Hang tight...",
          description: "button label",
        })}
        type="submit"
        width="full"
      >
        <FormattedMessage defaultMessage="Accept" description="button label" />
      </Button>
    </VStack>
  );
};

const InnerTOSForm = ({ onReady = noop, onComplete, onError }) => {
  const { hideExitButtons } = useFlowConfig();
  const { formatMessage } = useIntl();
  const { message: invalidLinkMessage } = useInvalidLinkCopy();
  const { error: apiError, isLoading: loading, isSuccess, data } = useTOSStatus();
  const { mutateAsync: signTOS } = useSignTOS();
  const toast = useToast();
  const [completed, setCompleted] = useState(false);

  // Completion fires at most once. Signing writes `signed_tos: true` straight into the query cache
  // (so one sign is visible to every reader), which means a successful submit satisfies the
  // already-signed effect below as well as its own explicit completion - without this guard the
  // flow would receive two `complete` callbacks for one acceptance. The ref is the guard rather
  // than `completed` itself, since the state update is not visible to a second call in the same
  // tick.
  const completeFired = useRef(false);
  const handleComplete = useCallback(
    (d) => {
      if (completeFired.current) return;
      completeFired.current = true;
      onComplete(d);
      setCompleted(true);
    },
    [onComplete],
  );

  useEffect(() => {
    onReady({ state: FLOW_STATE.READY });
  }, [onReady]);

  useEffect(() => {
    if (isSignedTOS(data)) {
      handleComplete({ state: "complete" });
    }
  }, [handleComplete, data]);

  // If we receive a 401 at this stage, it is highly likely that we are using an expired token for
  // the user: they waited long enough to open the link that the token in storage has expired.
  const tokenInvalid = apiError instanceof TiliaAPIError && apiError.status === 401;
  // Status unknown and not loading: the query is disabled, which only happens without a
  // sessionJWT. We cannot establish whether consent is needed, so fail closed rather than render a
  // checkbox over a status we never fetched. Same situation as an expired token, so the same copy.
  const statusUnavailable = !apiError && !loading && !isSuccess;

  const genericErrorMessage = formatMessage({
    defaultMessage: "Error retrieving invoice",
    description: "error message",
  });

  const errorReport = useMemo(() => {
    if (apiError) {
      return {
        message: tokenInvalid ? invalidLinkMessage : apiError.message || genericErrorMessage,
        error: apiError,
      };
    }
    if (statusUnavailable) {
      return { message: invalidLinkMessage, error: null };
    }
    return null;
  }, [apiError, tokenInvalid, statusUnavailable, invalidLinkMessage, genericErrorMessage]);

  // At most once, and from an effect rather than from the render body. `onError` leaves the widget
  // - it postMessages the integrator - so a render-phase call sends one notification per render
  // that lands in a failed branch, and these branches re-render for reasons unrelated to the
  // failure (a parent render, a locale change). The ref is the latch rather than state, for the
  // same reason as `completeFired` above: a state update is not visible to a second call in the
  // same tick.
  const errorFired = useRef(false);
  useEffect(() => {
    if (!errorReport || errorFired.current) return;
    errorFired.current = true;
    onError(errorReport);
  }, [errorReport, onError]);

  if (completed) {
    return <div data-testid={`tos-form-complete`}></div>;
  }
  if (apiError) {
    return tokenInvalid ? <InvalidLinkEmptyState /> : <EmptyState error={apiError} />;
  }
  if (isSignedTOS(data)) {
    return <div data-testid={`tos-form-result`}></div>;
  }
  if (loading) {
    return null;
  }
  if (statusUnavailable) {
    return <InvalidLinkEmptyState />;
  }

  const doSubmit = async () => {
    try {
      await signTOS();
      handleComplete({ state: "complete" });
    } catch (error) {
      console.error(error);

      let errorDescription = formatMessage({
        defaultMessage: "Something has gone wrong. Please try again later.",
        description: "error message",
      });
      if (error instanceof Error) {
        errorDescription = error.message;
      }

      toast({
        title: formatMessage({
          defaultMessage: "Oops!",
          description: "alert title",
        }),
        description: errorDescription,
        status: "error",
        duration: null,
        isClosable: true,
      });
    }
  };

  return (
    <Box minH={MIN_HEIGHT}>
      <VStack spacing="5" align="stretch">
        <Header
          onCloseButtonClick={!hideExitButtons && (() => handleComplete({ state: "cancel" }))}
          title={formatMessage({
            defaultMessage: "Tilia Terms Of Service",
            description: "Heading",
          })}
          showLanguage
        />
        <Formik
          initialValues={{
            tosAndPpCheckbox: false,
          }}
          initialErrors={{
            tosAndPpCheckbox: true,
          }}
          validationSchema={Yup.object().shape({
            tosAndPpCheckbox: Yup.bool().oneOf([true]),
          })}
          onSubmit={doSubmit}
          validateOnMount
        >
          {(props) => <FormContents {...props} />}
        </Formik>
      </VStack>
    </Box>
  );
};

const allowedBypassTOS = R.compose(isBooleanTrue, R.prop(ALLOWBYPASSTOS));
const requestBypassTOS = R.compose(isTrue, R.prop(REQUESTBYPASSTOS));

export const TOSForm = (props) => {
  const analytics = useAnalytics();
  const { config = {}, flow, onComplete } = props;
  const { debug, sessionCtx } = useSessionCtx();
  const { flowConfig = {} } = sessionCtx;
  const flowConfigSettings = config[flow] || {};
  debug("flowConfigSettings: ", flowConfigSettings);
  const viewEventFired = useRef(false);

  const allowedBypass = allowedBypassTOS(flowConfigSettings);
  const requestedBypass = requestBypassTOS(flowConfig);
  const finalBypass = allowedBypass && requestedBypass;
  debug("bypass tos? ", allowedBypass, requestedBypass, finalBypass);

  useEffect(() => {
    // useEffect for tracking page view analytic
    if (!viewEventFired.current) {
      analytics.page("TOS", "TOS_Viewed", { isBypassed: finalBypass });
      viewEventFired.current = true;
    }
  }, [analytics, viewEventFired, finalBypass]);

  useEffect(() => {
    if (finalBypass) {
      onComplete({ state: FLOW_STATE.BYPASS });
    }
  }, [finalBypass, debug, onComplete]);

  return finalBypass ? <div data-testid="tos-bypass" /> : <InnerTOSForm {...props} />;
};
